/**
 * Terms and Conditions Service
 * API client for T&C endpoints with offline support
 */

import { Platform } from 'react-native';
import { api } from './api';
import { networkService } from './network';
import { tcOfflineCache } from './tcOfflineCache';
import {
  Platform as TCPlatform,
  TCStatus,
  TCContent,
  AcceptanceInfo,
  TCStatusResponse,
  TCContentResponse,
  AcceptanceInfoResponse,
  AcceptTCResponse,
  TCVersion,
  TCVersionsResponse,
  CreateTCInput,
  UpdateTCInput,
  CreateTCResponse,
  UpdateTCResponse,
  PublishTCResponse,
  TCVersionStats,
  TCVersionStatsResponse,
} from '../types/termsConditions';

/**
 * Get the current platform string for API calls
 */
export function getCurrentPlatform(): TCPlatform {
  return Platform.OS as TCPlatform;
}

export class TermsConditionsService {
  /**
   * Get T&C status for the current user
   * Cross-platform: once accepted on any platform, valid everywhere
   * Falls back to cached data when offline
   */
  static async getStatus(): Promise<TCStatus> {
    const isOnline = networkService.isConnected();

    // If offline, try to return cached status
    if (!isOnline) {
      const cached = await tcOfflineCache.getCachedTCStatus();
      if (cached) {
        return cached;
      }
      // No cached data and offline - throw error
      throw new Error('Unable to get T&C status while offline. Please connect to the internet.');
    }

    try {
      const response = await api.get<TCStatusResponse>('/tc/status');
      // Cache the status for offline use
      await tcOfflineCache.cacheTCStatus(response.data);
      return response.data;
    } catch (error) {
      // If API call fails, try to return cached data
      const cached = await tcOfflineCache.getCachedTCStatus();
      if (cached) {
        return cached;
      }
      console.error('Error getting T&C status:', error);
      throw error;
    }
  }

  /**
   * Get the current active Terms and Conditions content
   * Falls back to cached content when offline
   */
  static async getCurrentTC(): Promise<TCContent> {
    const isOnline = networkService.isConnected();

    // If offline, try to return cached T&C content
    if (!isOnline) {
      const cached = await tcOfflineCache.getCachedCurrentTC();
      if (cached) {
        return cached;
      }
      // No cached data and offline - throw error
      throw new Error('Unable to load Terms and Conditions while offline. Please connect to the internet.');
    }

    try {
      const response = await api.get<TCContentResponse>('/tc/current');
      // Cache the T&C content for offline use
      await tcOfflineCache.cacheCurrentTC(response.data);
      await tcOfflineCache.setLastSyncTime();
      return response.data;
    } catch (error) {
      // If API call fails, try to return cached data
      const cached = await tcOfflineCache.getCachedCurrentTC();
      if (cached) {
        return cached;
      }
      console.error('Error getting current T&C:', error);
      throw error;
    }
  }

  /**
   * Accept Terms and Conditions
   * Note: This requires network connectivity - cannot be done offline
   */
  static async acceptTC(tcId: number, platform?: TCPlatform): Promise<void> {
    const isOnline = networkService.isConnected();

    if (!isOnline) {
      throw new Error('Cannot accept Terms and Conditions while offline. Please connect to the internet.');
    }

    try {
      const platformParam = platform || getCurrentPlatform();
      await api.post<AcceptTCResponse>('/tc/accept', {
        tcId,
        platform: platformParam,
      });
      // Update cached status after successful acceptance
      const cachedTC = await tcOfflineCache.getCachedCurrentTC();
      if (cachedTC) {
        await tcOfflineCache.updateStatusAfterAcceptance(tcId, cachedTC.version);
      }
    } catch (error) {
      console.error('Error accepting T&C:', error);
      throw error;
    }
  }

  /**
   * Get acceptance information for the current user
   * Cross-platform: returns acceptance info regardless of which platform it was accepted on
   * Falls back to cached data when offline
   */
  static async getAcceptanceInfo(): Promise<AcceptanceInfo> {
    const isOnline = networkService.isConnected();

    // If offline, try to return cached acceptance info
    if (!isOnline) {
      const cached = await tcOfflineCache.getCachedAcceptanceInfo();
      if (cached) {
        return cached;
      }
      // No cached data and offline - throw error
      throw new Error('Unable to get acceptance info while offline. Please connect to the internet.');
    }

    try {
      const response = await api.get<AcceptanceInfoResponse>('/tc/acceptance-info');
      // Cache the acceptance info for offline use
      await tcOfflineCache.cacheAcceptanceInfo(response.data);
      return response.data;
    } catch (error) {
      // If API call fails, try to return cached data
      const cached = await tcOfflineCache.getCachedAcceptanceInfo();
      if (cached) {
        return cached;
      }
      console.error('Error getting acceptance info:', error);
      throw error;
    }
  }

  // ============================================
  // Admin Operations (IT Administrator only)
  // ============================================

  /**
   * Get all T&C versions (IT Admin only)
   */
  static async getAllVersions(): Promise<TCVersion[]> {
    try {
      const response = await api.get<TCVersionsResponse>('/tc/admin/versions');
      return response.data;
    } catch (error) {
      console.error('Error getting all T&C versions:', error);
      throw error;
    }
  }

  /**
   * Create a new T&C version (IT Admin only)
   */
  static async createVersion(input: CreateTCInput): Promise<TCContent> {
    try {
      const response = await api.post<CreateTCResponse>('/tc/admin/create', input);
      return response.data;
    } catch (error) {
      console.error('Error creating T&C version:', error);
      throw error;
    }
  }

  /**
   * Update an existing T&C version (IT Admin only)
   */
  static async updateVersion(tcId: number, input: UpdateTCInput): Promise<TCContent> {
    try {
      const response = await api.put<UpdateTCResponse>(`/tc/admin/${tcId}`, input);
      return response.data;
    } catch (error) {
      console.error('Error updating T&C version:', error);
      throw error;
    }
  }

  /**
   * Publish/activate a T&C version (IT Admin only)
   * WARNING: This will force ALL users to re-accept the terms
   */
  static async publishVersion(tcId: number): Promise<void> {
    try {
      await api.post<PublishTCResponse>(`/tc/admin/${tcId}/publish`);
    } catch (error) {
      console.error('Error publishing T&C version:', error);
      throw error;
    }
  }

  /**
   * Get acceptance statistics for a T&C version (IT Admin only)
   */
  static async getVersionStats(tcId: number): Promise<TCVersionStats> {
    try {
      const response = await api.get<TCVersionStatsResponse>(`/tc/admin/${tcId}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error getting T&C version stats:', error);
      throw error;
    }
  }
}
